import React, { useState } from "react";
import {
  Globe,
  ChevronDown,
  ChevronRight,
  Code,
  Search,
  Loader2,
  X,
  Minimize2,
  Maximize2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";
import { config } from "@/config";

interface ApiCall {
  method: string;
  type?: string;
  url: string;
}

interface Action {
  action: string;
  apis: ApiCall[];
}

interface PersonaWorkflowItem {
  persona: string;
  outcome: string;
  scenario: string;
  step: string;
  actions: Action[];
}

interface FunctionalIntentItem {
  id: string;
  title: string;
}

interface PersonaWorkflowRendererProps {
  data: PersonaWorkflowItem[] | FunctionalIntentItem[];
}

const getMethodColor = (method: string) => {
  const colors = {
    GET: "bg-success/20 text-success border-success/30",
    POST: "bg-info/20 text-info border-info/30",
    PUT: "bg-primary/20 text-primary border-primary/30",
    DELETE: "bg-error/20 text-error border-error/30",
    PATCH: "bg-primary/20 text-primary border-primary/30",
    HEAD: "bg-muted/20 text-muted border-muted/30",
    OPTIONS: "bg-muted/20 text-muted border-muted/30",
  };
  return (
    colors[method as keyof typeof colors] ||
    "bg-muted/20 text-muted border-muted/30"
  );
};

const ApiAccordion: React.FC<{
  apis: ApiCall[];
  actionIndex: number;
  workflowIndex: number;
}> = ({ apis, actionIndex, workflowIndex }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<number, boolean>>(
    {}
  );
  const [apiDefinitions, setApiDefinitions] = useState<Record<number, any>>({});
  const accordionId = `api-accordion-${workflowIndex}-${actionIndex}`;

  const exploreApi = async (api: ApiCall, apiIndex: number) => {
    setLoadingStates((prev) => ({ ...prev, [apiIndex]: true }));

    try {
      const formData = new FormData();
      formData.append("input", api.url);
      formData.append("type", "private");
      formData.append("method", api.method);

      const response = await apiFetch(
        `${config.n8nUrl}/webhook/brandwatch/swagger/find`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        const definition = await response.json();
        setApiDefinitions((prev) => ({ ...prev, [apiIndex]: definition }));
      } else {
        console.error("Failed to fetch API definition");
        setApiDefinitions((prev) => ({
          ...prev,
          [apiIndex]: { error: "Failed to load definition" },
        }));
      }
    } catch (error) {
      console.error("Error exploring API:", error);
      setApiDefinitions((prev) => ({
        ...prev,
        [apiIndex]: { error: "Network error" },
      }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, [apiIndex]: false }));
    }
  };

  const closeApiDefinition = (apiIndex: number) => {
    setApiDefinitions((prev) => {
      const newState = { ...prev };
      delete newState[apiIndex];
      return newState;
    });
  };

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left text-muted text-xs font-medium flex items-center gap-2 hover:text-white transition-colors"
        aria-expanded={isExpanded}
        aria-controls={accordionId}
      >
        {isExpanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        <Globe className="w-3 h-3" />
        API Calls ({apis.length})
      </button>

      {isExpanded && (
        <div
          id={accordionId}
          className="grid gap-3 animate-in slide-in-from-top-2"
        >
          {apis.map((api, apiIndex) => (
            <div key={apiIndex} className="space-y-2">
              <div className="flex items-center gap-3 p-2 bg-background rounded border border-surface-border/20">
                {api.type && api.type !== "REST" && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                    {api.type}
                  </span>
                )}
                <span
                  className={cn(
                    "px-2 py-1 rounded text-xs font-mono font-semibold border",
                    getMethodColor(api.method)
                  )}
                >
                  {api.method}
                </span>
                <code className="text-white text-xs font-mono bg-surface-dark px-2 py-1 rounded flex-1 break-all">
                  {api.url}
                </code>
                <button
                  onClick={() => exploreApi(api, apiIndex)}
                  disabled={loadingStates[apiIndex]}
                  className="flex items-center gap-1 px-2 py-1 bg-primary hover:bg-primary-dark disabled:opacity-50 text-white text-xs rounded transition-colors"
                  title="Explore API"
                >
                  {loadingStates[apiIndex] ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Search className="w-3 h-3" />
                  )}
                  Explore
                </button>
              </div>

              {apiDefinitions[apiIndex] && (
                <div className="ml-4 p-3 bg-surface-input rounded border border-surface-border/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-primary text-xs font-medium">
                      API Definition:
                    </div>
                    <button
                      onClick={() => closeApiDefinition(apiIndex)}
                      className="p-1 hover:bg-surface-border/30 rounded transition-colors"
                      title="Close definition"
                    >
                      <X className="w-3 h-3 text-muted hover:text-white" />
                    </button>
                  </div>
                  <pre className="text-xs text-white bg-surface-dark p-2 rounded overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(apiDefinitions[apiIndex], null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PersonaWorkflowRenderer: React.FC<PersonaWorkflowRendererProps> = ({
  data,
}) => {
  const [isMinimized, setIsMinimized] = useState(true);
  const [expandedData, setExpandedData] = useState<
    Record<string, PersonaWorkflowItem[] | null>
  >({});
  const [loadingExpansion, setLoadingExpansion] = useState<
    Record<string, boolean>
  >({});
  const [expansionError, setExpansionError] = useState<
    Record<string, string | null>
  >({});

  if (!data || data.length === 0) {
    return (
      <div className="bg-surface-card rounded-lg p-4 text-muted">
        No workflow data available
      </div>
    );
  }

  // Type guard to check if data is the new format (FunctionalIntentItem[])
  const isNewFormat = (
    data: PersonaWorkflowItem[] | FunctionalIntentItem[]
  ): data is FunctionalIntentItem[] => {
    return data.length > 0 && "id" in data[0] && "title" in data[0];
  };

  // Function to fetch detailed persona workflow data for a single item
  const fetchDetailedData = async (item: FunctionalIntentItem) => {
    const itemId = item.id;
    setLoadingExpansion((prev) => ({ ...prev, [itemId]: true }));
    setExpansionError((prev) => ({ ...prev, [itemId]: null }));

    try {
      // Create FormData for the API call
      const formData = new FormData();
      formData.append("nodes", itemId);
      formData.append("query", item.title);

      const response = await apiFetch(
        `${config.n8nUrl}/webhook/brandwatch/node`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(
          `API request failed: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();

      // Check if response has output key and handle null case
      if (!responseData.output || responseData.output === null) {
        throw new Error("No node data found for the provided ID");
      }

      const outputData = responseData.output;

      // Convert single object to array for consistent handling
      const detailedData = Array.isArray(outputData)
        ? outputData
        : [outputData];

      // Validate that the response is in the expected PersonaWorkflowItem format
      if (detailedData.length > 0) {
        // Basic validation to ensure it looks like PersonaWorkflowItem[]
        const isValidFormat = detailedData.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "persona" in item &&
            "outcome" in item &&
            "scenario" in item
        );

        if (isValidFormat) {
          setExpandedData((prev) => ({ ...prev, [itemId]: detailedData }));
        } else {
          throw new Error(
            "API response is not in the expected PersonaWorkflowItem format"
          );
        }
      } else {
        throw new Error("API response output is empty");
      }
    } catch (error) {
      console.error("Failed to fetch detailed data:", error);
      setExpansionError((prev) => ({
        ...prev,
        [itemId]:
          error instanceof Error
            ? error.message
            : "Failed to fetch detailed data",
      }));
    } finally {
      setLoadingExpansion((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  // Handle new format
  if (isNewFormat(data)) {
    if (isMinimized) {
      return (
        <div className="bg-surface-card border border-surface-border rounded-lg p-3 my-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code className="h-3 w-3 text-primary" />
              <span className="text-xs font-medium text-white">
                Functional Intent ({data.length} item
                {data.length !== 1 ? "s" : ""})
              </span>
            </div>
            <button
              onClick={() => setIsMinimized(false)}
              className="flex items-center gap-1 text-xs text-muted hover:text-white transition-colors"
            >
              <span>Expand</span>
              <Maximize2 className="h-3 w-3" />
            </button>
          </div>

          <div className="mt-2 space-y-1">
            {data.map((item) => (
              <div key={item.id} className="text-xs text-muted">
                <span className="text-primary">ID:</span> {item.id} -{" "}
                {item.title}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-primary font-semibold text-lg">
            <Code className="w-5 h-5" />
            Functional Intent
          </div>
          <button
            onClick={() => setIsMinimized(true)}
            className="flex items-center gap-1 text-xs text-muted hover:text-white transition-colors"
            title="Minimize view"
          >
            <span>Minimize</span>
            <Minimize2 className="h-3 w-3" />
          </button>
        </div>

        {/* Individual cards for each functional intent item */}
        <div className="space-y-4">
          {data.map((item) => {
            const itemId = item.id;
            const isLoading = loadingExpansion[itemId];
            const hasError = expansionError[itemId];
            const hasExpandedData = expandedData[itemId];

            return (
              <div
                key={itemId}
                className="bg-surface-card border border-surface-border rounded-lg p-4"
              >
                {/* Header with title and expand button */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-white font-medium text-base mb-2">
                      {item.title}
                    </h3>
                    <div className="text-xs text-muted">
                      <span className="text-primary">ID:</span> {itemId}
                    </div>
                  </div>
                  <button
                    onClick={() => fetchDetailedData(item)}
                    disabled={isLoading || !!hasExpandedData}
                    className="flex items-center gap-1 text-xs bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1 rounded transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span>Loading...</span>
                      </>
                    ) : hasExpandedData ? (
                      <>
                        <span>Expanded</span>
                      </>
                    ) : (
                      <>
                        <span>Expand</span>
                        <Maximize2 className="h-3 w-3" />
                      </>
                    )}
                  </button>
                </div>

                {/* Show error state for this specific item */}
                {hasError && (
                  <div className="bg-error/10 border border-error/30 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-error rounded-full"></div>
                      <div className="text-error font-semibold text-sm">
                        Failed to Load Details
                      </div>
                    </div>
                    <div className="text-muted text-xs mb-2">{hasError}</div>
                    <button
                      onClick={() => fetchDetailedData(item)}
                      className="text-xs bg-primary hover:bg-primary-dark text-white px-2 py-1 rounded transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Show detailed data for this specific item */}
                {hasExpandedData && (
                  <div className="space-y-4 border-t border-surface-border pt-4">
                    {hasExpandedData.map((workflow, workflowIndex) => (
                      <div key={workflowIndex}>
                        {/* DETAILS Section */}
                        <div className="mb-4 bg-surface-input rounded-lg p-3">
                          <h4 className="text-muted text-xs font-medium uppercase tracking-wide mb-3">
                            DETAILS
                          </h4>
                          <div className="space-y-2">
                            <div className="flex justify-between items-start">
                              <span className="text-muted text-xs">
                                Persona:
                              </span>
                              <span className="text-white text-xs text-right">
                                {workflow.persona}
                              </span>
                            </div>
                            <div className="flex justify-between items-start">
                              <span className="text-muted text-xs">
                                Outcome:
                              </span>
                              <span className="text-white text-xs text-right">
                                {workflow.outcome}
                              </span>
                            </div>
                            <div className="flex justify-between items-start">
                              <span className="text-muted text-xs">
                                Scenario:
                              </span>
                              <span className="text-white text-xs text-right">
                                {workflow.scenario}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Steps & Actions Section */}
                        <div>
                          <h4 className="text-muted text-xs font-medium uppercase tracking-wide mb-3">
                            STEPS & ACTIONS
                          </h4>
                          <div className="text-white font-medium text-sm mb-3">
                            {workflow.step}
                          </div>

                          {/* Actions - Numbered circles with clean text */}
                          <div className="space-y-2">
                            {workflow.actions.map((action, actionIndex) => {
                              // Remove numbering patterns from action text (e.g., "1. ", "1) ", "Step 1: ", etc.)
                              const cleanActionText = action.action
                                .replace(
                                  /^(\d+[\.\)\:]?\s*|Step\s*\d+[\.\:\-\s]*)/i,
                                  ""
                                )
                                .trim();

                              return (
                                <div key={actionIndex} className="flex gap-3">
                                  <div className="flex-shrink-0 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-white text-xs font-medium">
                                    {actionIndex + 1}
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-white text-xs mb-1">
                                      {cleanActionText}
                                    </div>

                                    {action.apis.length > 0 && (
                                      <ApiAccordion
                                        apis={action.apis}
                                        actionIndex={actionIndex}
                                        workflowIndex={workflowIndex}
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div className="bg-surface-card border border-surface-border rounded-lg p-3 my-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Code className="h-3 w-3 text-primary" />
            <span className="text-xs font-medium text-white">
              Functional Intent ({data.length} workflow
              {data.length !== 1 ? "s" : ""})
            </span>
          </div>
          <button
            onClick={() => setIsMinimized(false)}
            className="flex items-center gap-1 text-xs text-muted hover:text-white transition-colors"
          >
            <span>Expand</span>
            <Maximize2 className="h-3 w-3" />
          </button>
        </div>

        <div className="mt-2 space-y-1">
          {data.map((workflow, index) => (
            <div key={index} className="text-xs text-muted">
              <span className="text-primary">{workflow.persona}:</span>{" "}
              {workflow.outcome}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-primary font-semibold text-lg">
          <Code className="w-5 h-5" />
          Functional Intent Disambiguation
        </div>
        <button
          onClick={() => setIsMinimized(true)}
          className="flex items-center gap-1 text-xs text-muted hover:text-white transition-colors"
          title="Minimize view"
        >
          <span>Minimize</span>
          <Minimize2 className="h-3 w-3" />
        </button>
      </div>

      {data.map((workflow, workflowIndex) => (
        <div
          key={workflowIndex}
          className="bg-surface-card border border-surface-border rounded-lg p-6"
        >
          {/* DETAILS Section */}
          <div className="mb-3 bg-surface-input rounded-lg p-4">
            <h4 className="text-muted text-xs font-medium uppercase tracking-wide mb-4">
              DETAILS
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-muted text-sm">Persona:</span>
                <span className="text-white text-sm text-right">
                  {workflow.persona}
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-muted text-sm">Outcome:</span>
                <span className="text-white text-sm text-right">
                  {workflow.outcome}
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-muted text-sm">Scenario:</span>
                <span className="text-white text-sm text-right">
                  {workflow.scenario}
                </span>
              </div>
            </div>
          </div>

          {/* Steps & Actions Section */}
          <div>
            <h4 className="text-muted text-xs font-medium uppercase tracking-wide mb-4">
              STEPS & ACTIONS
            </h4>
            <div className="text-white font-medium text-base mb-4">
              {workflow.step}
            </div>

            {/* Actions - Numbered circles with clean text */}
            <div className="space-y-3">
              {workflow.actions.map((action, actionIndex) => {
                // Remove numbering patterns from action text (e.g., "1. ", "1) ", "Step 1: ", etc.)
                const cleanActionText = action.action
                  .replace(/^(\d+[\.\)\:]?\s*|Step\s*\d+[\.\:\-\s]*)/i, "")
                  .trim();

                return (
                  <div key={actionIndex} className="flex gap-4">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-medium">
                      {actionIndex + 1}
                    </div>
                    <div className="flex-1">
                      <div className="text-white text-sm mb-2">
                        {cleanActionText}
                      </div>

                      {action.apis.length > 0 && (
                        <ApiAccordion
                          apis={action.apis}
                          actionIndex={actionIndex}
                          workflowIndex={workflowIndex}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PersonaWorkflowRenderer;
